import events from 'events.js';
import http from 'http.js';
import ko from 'knockout';
import {page} from 'page.js';
import user from 'accounts/models/user.js';

import 'ko/elements/autocomplete.js';

const baseUrl = '/api/professionals/v1/';

class ReviewManager {
    constructor() {
        const dataset = document.body.dataset;

        this.kiosk = dataset.kiosk;
        this.bedsideManner = ko.observable().extend({required: true});
        this.conditionUnderstanding = ko.observable().extend({required: true});
        this.treatmentCommunication = ko.observable().extend({required: true});
        this.wouldRecommend = ko.observable(true).extend({required: true});
        this.locality = ko.observable().extend({required: true});
        this.reviewErrors = ko.validation.group([
            this.bedsideManner,
            this.conditionUnderstanding,
            this.treatmentCommunication,
            this.locality,
            this.reviewErrors,
        ]);
        this.comments = ko.observable();

        this.currentStep = ko.observable();
        this.authType = ko.observable('login');
        this.registering = ko.observable(false);
        this.termsAgree = ko.observable(false);

        this.submitting = ko.observable(false);
        this.user = user;
        this.practitionerProfileId = parseInt(dataset.practitionerProfileId);
        this.practiceInfo = dataset.practiceInfo;
        this.validationErrors = ko.observableArray();

        if (this.practiceInfo) {
            this.redirectUrl = ko.observable(
                `/ratings/?${this.practiceInfo}&kiosk=1`,
            );
        } else {
            this.redirectUrl = ko.observable(window.location.href);
        }

        this.authenticate = this.authenticate.bind(this);
    }

    authenticate() {
        this.currentStep('authenticate');
    }

    async doesReviewExist() {
        const url =
            `${baseUrl}${this.practitionerProfileId}/reviews/` +
            `?user=${this.user.id()}`;

        const response = await http.get({url});
        return response.count > 0;
    }

    async load() {
        try {
            await this.user.load();
        } catch (ex) {}

        if (this.user.username()) {
            if (this.user.userType() !== 'P') {
                this.currentStep('invalid-account');
            } else if (await this.doesReviewExist()) {
                this.currentStep('review-exists');
            } else {
                this.currentStep('practitioner-review');
            }
        } else {
            this.currentStep('practitioner-review');
        }

        ko.applyBindings(this);
    }

    async login() {
        try {
            await this.user.login();
            page.alert(page.alertTypes.SUCCESS, 'Login successful.');
        } catch (ex) {
            page.alert(page.alertTypes.ERROR, 'Login failed.');
            return;
        }

        if (this.user.userType() !== 'P') {
            this.currentStep('invalid-account');
        } else if (await this.doesReviewExist()) {
            this.currentStep('review-exists');
        } else {
            this.claimReview();
        }
    }

    async register() {
        this.user.registrationUsername.rules.remove(
            (item) => item.rule === 'required',
        );
        this.registering(true);
        try {
            await this.user.registerAndLogin();
            page.alert(page.alertTypes.SUCCESS, 'Signup successful.');
            this.claimReview();
        } catch (ex) {
            page.alert(page.alertTypes.ERROR, 'Member signup failed.');
        } finally {
            this.registering(false);
        }
    }

    async submit() {
        if (this.reviewErrors().length) {
            this.reviewErrors.showAllMessages();
            return;
        }

        const url = `${baseUrl}${this.practitionerProfileId}/reviews/`;

        const reviewData = {
            member: this.user.profileId(),
            locality: this.locality().id,
            comments: this.comments(),
            would_recommend: this.wouldRecommend(),
        };

        try {
            this.submitting(true);
            // Submit reviews
            const response = await http.post({
                url,
                data: reviewData,
            });

            this.reviewId = response.id;
            this.ratingUrl = `${url}${response.id}/ratings/`;

            this.ratings = [
                {'name': 'B', 'rating': this.bedsideManner()},
                {'name': 'C', 'rating': this.conditionUnderstanding()},
                {'name': 'T', 'rating': this.treatmentCommunication()},
            ];
            await this.submitRatings();
            this.currentStep('success');
        } catch (ex) {
            page.alert(
                page.alertTypes.ERROR,
                'Error making request. Please try again.',
                8000,
            );
        } finally {
            this.submitting(false);
            if (this.kiosk) {
                this.createRedirect(5000);
            }
        }
    }

    async submitRatings() {
        for (const i in this.ratings) {
            http.post({
                url: this.ratingUrl,
                data: this.ratings[i],
            });
        }
    }

    async claimReview() {
        // eslint-disable-next-line max-len
        const url = `${baseUrl}${this.practitionerProfileId}/reviews/${this.reviewId}/`;
        try {
            await http.patch({url});
            this.currentStep('success');
        } catch (ex) {
            page.alert(
                page.alertTypes.ERROR,
                'Error linking review to account.',
                3000,
            );
        }
    }

    createRedirect(time) {
        return setTimeout(() => {
            window.location = this.redirectUrl();
        }, time);
    }
}

(async function () {
    await events.ready();
    new ReviewManager().load();
})();
